import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Extend dayjs with timezone support
dayjs.extend(utc)
dayjs.extend(timezone)

const PACIFIC_TIMEZONE = 'America/Los_Angeles'

type DateInput = Date | string | null | undefined

/**
 * Format a date to Pacific Time for display
 */
export const formatToPacificTime = (date: DateInput): string => {
    if (!date) return 'N/A'
    return dayjs(date).tz(PACIFIC_TIMEZONE).format('MMM D, YYYY h:mm A') + ' PT'
}

/**
 * Second-precision Pacific time, for dense technical logs where consecutive
 * entries can land within the same minute.
 */
export const formatToPacificTimeWithSeconds = (date: DateInput): string => {
    if (!date) return 'N/A'
    return dayjs(date).tz(PACIFIC_TIMEZONE).format('MMM D, h:mm:ss A')
}

/**
 * Format a date to Pacific Time, returning date and time as separate strings.
 * Useful for wrapping each part in a no-wrap span to control line breaks.
 */
export const formatPacificDateTimeParts = (date: DateInput): { date: string; time: string } | null => {
    if (!date) return null
    const d = dayjs(date).tz(PACIFIC_TIMEZONE)
    return {
        date: d.format('MMM D, YYYY'),
        time: d.format('h:mm A') + ' PT',
    }
}

/**
 * Format a date to venue local time for display
 */
export const formatToVenueTime = (
    date: DateInput,
    venueTimezone: string | null | undefined
): string => {
    if (!date) return 'N/A'
    if (!venueTimezone) return 'Local time unavailable'

    try {
        return dayjs(date).tz(venueTimezone).format('MMM D, YYYY h:mm A')
    } catch (error) {
        console.error('Invalid timezone:', venueTimezone, error)
        return 'Invalid timezone'
    }
}

/**
 * Convert a datetime-local input value (in PT) to UTC ISO string for API
 */
export const convertPTInputToUTC = (dateTimeLocalValue: string): string => {
    if (!dateTimeLocalValue) return ''
    // datetime-local value is in format: YYYY-MM-DDTHH:mm
    // Treat it as Pacific Time and convert to UTC
    return dayjs.tz(dateTimeLocalValue, PACIFIC_TIMEZONE).utc().toISOString()
}

/**
 * Convert a UTC date to datetime-local input value in PT
 */
export const convertUTCToPTInput = (date: DateInput): string => {
    if (!date) return ''
    try {
        return dayjs(date).tz(PACIFIC_TIMEZONE).format('YYYY-MM-DDTHH:mm')
    } catch {
        return ''
    }
}

/**
 * Get "now" as a datetime-local value in PT (YYYY-MM-DDTHH:mm)
 */
export const nowAsPTInput = (): string => {
    return dayjs().tz(PACIFIC_TIMEZONE).format('YYYY-MM-DDTHH:mm')
}

/**
 * Get a PT datetime-local value offset by a number of days from a base PT input (or now)
 */
export const offsetPTInput = (basePTInput: string | null, days: number): string => {
    const base = basePTInput
        ? dayjs.tz(basePTInput, PACIFIC_TIMEZONE)
        : dayjs().tz(PACIFIC_TIMEZONE)
    return base.add(days, 'day').startOf('day').format('YYYY-MM-DDTHH:mm')
}

/**
 * Get the timezone abbreviation for a given timezone at a specific date
 */
export const getTimezoneAbbr = (
    date: DateInput,
    tz: string | null | undefined
): string => {
    if (!date || !tz) return ''

    try {
        const d = dayjs(date).tz(tz)
        const offset = d.format('Z')
        return `(UTC${offset})`
    } catch {
        return ''
    }
}
